import React, { useCallback, useEffect, useState } from "react";
import { Activity, RotateCcw, Server } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { api } from "@/lib/api";

const hhmm = (iso) => new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
const dateTime = (iso) => new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
const shortHost = (h = "") => (h.length > 18 ? `…${h.slice(-16)}` : h);
const tone = (pct, th) => (pct >= th ? "bg-findo-red" : pct >= th - 15 ? "bg-amber-500" : "bg-emerald-500");

function InstanceGauge({ s, th, i }) {
  return (
    <div className="rounded-xl border border-border p-3" data-testid={`server-health-instance-${i}`}>
      <div className="flex items-center justify-between gap-2 text-xs text-slate-500">
        <span className="inline-flex items-center gap-1.5 font-semibold min-w-0 truncate" title={s.host}><Server size={13} className="shrink-0" /> {shortHost(s.host)}</span>
        <span className="whitespace-nowrap">{s.used_mb} / {s.limit_mb} Mo</span>
      </div>
      <p className="mt-1 font-display text-2xl font-bold text-findo-navy" data-testid={`server-health-pct-${i}`}>{Math.round(s.pct)} %</p>
      <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full transition-[width] duration-700 ${tone(s.pct, th)}`} style={{ width: `${Math.min(100, s.pct)}%` }} />
      </div>
      <p className="mt-1.5 text-[11px] text-slate-400">Processus API : {s.rss_mb} Mo</p>
    </div>
  );
}

function RestartList({ restarts }) {
  if (!restarts.length) return <p className="text-sm text-slate-400" data-testid="server-health-no-restarts">Aucun redémarrage sur 7 jours.</p>;
  return (
    <ul className="divide-y divide-border text-sm" data-testid="server-health-restarts">
      {restarts.map((r, i) => (
        <li key={`${r.instance}-${i}`} className="flex items-center justify-between gap-3 py-1.5" data-testid={`server-health-restart-${i}`}>
          <span className="text-slate-700 whitespace-nowrap">{dateTime(r.started_at)}</span>
          <span className="text-xs text-slate-500 truncate min-w-0" title={r.host}>{shortHost(r.host)}</span>
          {r.peak_before_pct != null
            ? <span className={`text-xs font-bold whitespace-nowrap ${r.peak_before_pct >= 80 ? "text-findo-red" : "text-slate-500"}`}>avant : {Math.round(r.peak_before_pct)} %</span>
            : <span className="text-xs text-slate-300">—</span>}
        </li>
      ))}
    </ul>
  );
}

export default function ServerHealthCard() {
  const [data, setData] = useState(null);
  const load = useCallback(() => api("/admin/server-health").then(setData).catch(() => {}), []);
  useEffect(() => { load(); const iv = setInterval(load, 60000); return () => clearInterval(iv); }, [load]);
  if (!data) return null;
  const th = data.threshold_pct;
  return (
    <section id="server-health-card" className="mb-6 bg-white rounded-2xl border border-border card-shadow p-5" data-testid="server-health-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="font-display font-semibold text-findo-navy flex items-center gap-2"><Activity size={17} className="text-findo-indigo" /> Santé serveur — mémoire</h3>
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500" data-testid="server-health-restarts-24h">
          <RotateCcw size={13} /> {data.restarts_24h} redémarrage(s) sur 24 h · alerte au-delà de {th} %
        </span>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {data.instances.length
          ? data.instances.map((s, i) => <InstanceGauge key={s.host} s={s} th={th} i={i} />)
          : <p className="text-sm text-slate-400" data-testid="server-health-waiting">Première mesure dans moins d'une minute…</p>}
      </div>
      <div className="mt-5 grid gap-5 lg:grid-cols-3">
        <div className="lg:col-span-2 h-44" data-testid="server-health-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.series} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
              <XAxis dataKey="at" tickFormatter={hhmm} tick={{ fontSize: 11 }} minTickGap={40} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
              <Tooltip labelFormatter={dateTime} formatter={(v, n) => (n === "pct" ? [`${v} %`, "Pic mémoire"] : [v, n])} />
              <ReferenceLine y={th} stroke="#e5484d" strokeDasharray="4 4" />
              <Area type="monotone" dataKey="pct" stroke="#3b4bd8" fill="#3b4bd8" fillOpacity={0.12} strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-1.5">Redémarrages (7 jours)</p>
          <RestartList restarts={data.restarts} />
        </div>
      </div>
    </section>
  );
}
